import * as React from 'react';
import {
    GatsbyImage,
    type GatsbyImageProps,
    getImage,
} from 'gatsby-plugin-image';
import type { FileNodeOrSvg, ResponsiveImage, SvgImage } from 'lib/types';
import { getImages } from 'lib/util';
import * as styles from './ImageWrapper.module.scss';

export type ImageWrapperProps = Omit<GatsbyImageProps, 'image'> & {
    image?: FileNodeOrSvg | ResponsiveImage | null;
    useSafariImageFix?: boolean;
};

// Type checks
function isResponsiveImage(
    image: FileNodeOrSvg | ResponsiveImage | null,
): boolean {
    return (
        (image as ResponsiveImage)?.desktop !== undefined ||
        (image as ResponsiveImage)?.mobile !== undefined
    );
}

function isSvgImage(image: FileNodeOrSvg | null): image is SvgImage {
    return (
        (image as SvgImage)?.publicURL !== undefined &&
        /\.(svg|svg\+xml|svgz)$/i.test((image as SvgImage).publicURL)
    );
}

/**
 * Wrapper for GatsbyImage to fetch images and properly display SVGs.
 * Requires just the results from the GraphQL query.
 */
function ImageWrapper({
    image = null,
    useSafariImageFix = false,
    children,
    ...props
}: React.PropsWithChildren<ImageWrapperProps>) {
    if (isSvgImage(image)) {
        return (
            <div className={props.className}>
                <img
                    {...(props as unknown as React.DetailedHTMLProps<
                        React.ImgHTMLAttributes<HTMLImageElement>,
                        HTMLImageElement
                    >)}
                    className={useSafariImageFix ? styles.imageFix : ''}
                    src={image.publicURL ?? ''}
                    alt={props.alt}
                />
            </div>
        );
    }

    /*
     We need to call different functions for ResponsiveImage or FileNodes.
    */
    const gatsbyImage = isResponsiveImage(image)
        ? getImages(image as ResponsiveImage)
        : getImage(image);

    if (gatsbyImage) {
        return (
            <GatsbyImage {...props} image={gatsbyImage}>
                {children}
            </GatsbyImage>
        );
    }

    return null;
}

export default ImageWrapper;
